import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'

const EMOJI_CATEGORIES = [
  {
    label: 'Activity',
    emojis: [
      '🏃',
      '🚴',
      '🏊',
      '🏋️',
      '🧘',
      '🧗',
      '🚶',
      '🧍',
      '⚽',
      '🏀',
      '🏸',
      '🥊',
      '🎯',
    ],
  },
  {
    label: 'Health',
    emojis: [
      '💧',
      '🥗',
      '🍎',
      '🍌',
      '🍓',
      '☕',
      '🥤',
      '🛌',
      '😴',
      '💊',
      '💪',
      '🧼',
      '🦷',
    ],
  },
  {
    label: 'Mind & Learning',
    emojis: ['📚', '✍️', '🎨', '🎵', '🧩', '💻', '📝', '🧠', '📖', '🪄'],
  },
  {
    label: 'Routine',
    emojis: ['🗓️', '⏰', '✅', '🔁', '🌿', '🧴', '🧹', '🧺', '🛎️', '🔔'],
  },
  {
    label: 'Motivation',
    emojis: ['❤️', '⭐', '🔥', '🎉', '🌟', '🙏', '💡', '🤩', '🏆', '⚡'],
  },
]

interface EmojiNameInputProps {
  inputRef: React.RefObject<HTMLInputElement | null>
  value: string
  onChange: (value: string) => void
}

export function EmojiNameInput({
  inputRef,
  value,
  onChange,
}: EmojiNameInputProps): ReactElement {
  const [open, setOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutside(e: MouseEvent): void {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  function insertEmoji(emoji: string): void {
    const next = `${emoji} ${value}`
    onChange(next)
    requestAnimationFrame(() => {
      const input = inputRef.current
      if (input) {
        input.focus()
        const pos = emoji.length + 1
        input.setSelectionRange(pos, pos)
      }
    })
    setOpen(false)
  }

  return (
    <div className="relative">
      <div className="flex gap-1.5">
        <input
          ref={inputRef}
          id="habit-name"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. 🏃 Morning run"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          required
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Pick emoji"
          className="shrink-0 rounded-lg border border-gray-300 px-2.5 py-2 text-xl hover:bg-gray-100"
        >
          😊
        </button>
      </div>
      {open && (
        <div
          ref={pickerRef}
          className="absolute right-0 z-10 mt-1 w-[calc(100vw-2rem)] max-w-xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg sm:w-[24rem]"
        >
          <div className="max-h-96 space-y-3 overflow-y-auto p-3">
            {EMOJI_CATEGORIES.map((category) => (
              <div key={category.label}>
                <div className="mb-2 px-1 font-semibold text-gray-500 text-xs uppercase tracking-wide">
                  {category.label}
                </div>
                <div className="grid grid-cols-8 gap-1">
                  {category.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-lg hover:bg-gray-100"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
